<template>
  <Teleport v-if="showWarning" :to="target" :disabled="!target">
    <Alert
      type="error"
      class="form-safety-alert"
      :class="{ 'form-safety-alert--in-heading': target }"
    >
      <template #title>{{ $t(`formSafetyAlert.${mode}Title`) }}</template>
      <p>{{ $t(`formSafetyAlert.${mode}Description`) }}</p>
      <template v-if="mode === 'editor'" #actions>
        <a
          :href="router.resolve({ name: 'subscriptions' }).href"
          target="_blank"
        >
          {{ $t('formSafetyAlert.upgrade') }}
        </a>
        <a :href="router.resolve({ name: 'contact' }).href" target="_blank">
          {{ $t('formSafetyAlert.contact') }}
        </a>
      </template>
    </Alert>
  </Teleport>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useNuxtApp } from '#app'
import debounce from 'lodash/debounce'
import FormSafetyService from '@saas/services/formSafety'

const props = defineProps({
  slug: {
    type: String,
    required: true,
  },
  mode: {
    type: String,
    required: true,
    validator: (value) => ['public', 'editor'].includes(value),
  },
  /**
   * Changes when the form fields are edited, so that the editor knows when the
   * warning must be checked again.
   */
  refreshKey: {
    type: String,
    required: false,
    default: '',
  },
  /**
   * Returns the element the alert is moved into, so that it can be shown below the
   * form description. If it returns nothing, the alert is shown in place.
   */
  getTarget: {
    type: Function,
    required: false,
    default: () => null,
  },
})

const { $client, $store } = useNuxtApp()
const router = useRouter()
const showWarning = ref(false)
const target = ref(null)
// Responses can arrive out of order when the form is edited quickly, so only the
// last request may update the alert.
let lastRequestId = 0

async function fetchShowWarning() {
  const requestId = ++lastRequestId
  try {
    // Needed for password protected forms, otherwise the form can't be checked.
    const publicAuthToken =
      props.mode === 'public'
        ? $store.getters['page/view/public/getAuthToken']
        : null
    const { data } = await FormSafetyService($client).get(
      props.slug,
      publicAuthToken
    )
    if (requestId !== lastRequestId) {
      return
    }
    // Only resolved after the response because the target is rendered by the form
    // component after this alert.
    target.value = props.getTarget() || null
    showWarning.value = data.show_warning
  } catch {
    if (requestId === lastRequestId) {
      showWarning.value = false
    }
  }
}

const debouncedFetchShowWarning = debounce(fetchShowWarning, 1000)

onMounted(fetchShowWarning)
watch(() => props.slug, fetchShowWarning)
watch(() => props.refreshKey, debouncedFetchShowWarning)
onBeforeUnmount(() => debouncedFetchShowWarning.cancel())
</script>
